import { supabase } from './supabaseClient';

/**
 * Die Schema-Wache: haelt der ausgelieferte Client den Datenbankstand fuer
 * moeglich, den er vorfindet?
 *
 * Anlass ist der teuerste wiederkehrende Fehler dieses Projekts: Client
 * ausgerollt, das zugehoerige SQL vergessen — und die Umgebung laedt kein
 * Projekt mehr (zuletzt Nr. 50 auf TEST). Der Stand steht seit Migration
 * Nr. 57 in `public.schema_migrations` (MIGRATIONS.md), und diese Datei ist
 * die einzige Stelle im Client, die ihn liest.
 *
 * Die REGELN liegen darunter in `bewerteSchemaStand` und sind ohne Supabase
 * pruefbar; hier steht nur, was ohne Supabase nicht zu haben ist. Das ist die
 * Linie des Repos: duenne Container, Logik in reinen Funktionen — es gibt
 * kein jsdom im Projekt, alles andere waere ungetestet.
 */

/**
 * Der Datenbankstand, den dieser Client-Stand voraussetzt.
 *
 * VON HAND hochgesetzt, und zwar bewusst: nicht jede Migration betrifft den
 * Client. Nr. 57 selbst legt nur die Buchfuehrung dieses Runners an, andere
 * raeumen Altbestand auf oder ruesten Rechte nach — nichts davon darf einen
 * Warnbalken erzeugen. Ein Automatismus („nimm die hoechste Nummer im
 * Verzeichnis") wuerde genau das tun und die Warnung damit binnen Wochen zu
 * Rauschen machen, den man wegsieht. Die Zahl steigt nur, wenn der Code
 * wirklich auf etwas Neues angewiesen ist — und dann ist das Hochsetzen Teil
 * derselben Aenderung.
 *
 * GENAU DAS ist bei Nr. 68 nicht passiert: der Client waehlte
 * `project_services.transferred_from` bereits mit, hier stand weiter 57, und
 * die Wache zeigte waehrend des Ausfalls vom 02.09.2026 nichts an. Die Zahl
 * ist deshalb nachgezogen (zuletzt auf 185: Online-Freimeldung — die
 * QR-Seite ruft `report_freimeldung_zurueckweisen` aus Nr. 185 (ohne sie
 * PGRST202, „Zurückweisung absenden" meldet einen allgemeinen Fehler), und
 * Nr. 183 liest ein Projekt ohne Schalter als freigeschaltet, wie
 * `istFreimeldungAktiv` (ohne sie weist `link_setzen` mit 22023 ab und der
 * Bericht geht fail-open ohne QR raus); die Spalte `art` aus Nr. 184 liest
 * der Client ueber `select('*')` und deutet ihr Fehlen als „behoben". Davor
 * 172: Stundenkontingent fuer
 * Paketpositionen — der Client waehlt `project_services.stunden_kontingent`
 * und `project_services.stunden_erfasst` in PROJECT_SERVICES_COLUMNS mit
 * (Spalten aus Nr. 171; fehlen sie, faellt er ueber das Rueckfallregister
 * auf „ohne Stunden" zurueck — die Spaltenwache im Test unten sieht Nr. 171)
 * und ruft `bestandteil_erledigen` / `bestandteil_entfaellt` mit
 * `p_stunden` sowie die neuen RPCs `bestandteil_stunden_setzen` und
 * `position_stundenkontingent_setzen` aus Nr. 172 (ohne sie PGRST202 —
 * auch der Aufruf mit `p_stunden` findet ohne Nr. 172 keine Funktion); erst
 * Nr. 172 rechnet `stunden_erfasst` und haelt das Kontingent gegen alte Tabs
 * fest (S10). Davor 170: Plotter-Papierrolle — der
 * Client liest `plot_druck_nachtraege` mit namentlicher Spaltenliste
 * (`PLOT_NACHTRAG_SPALTEN`, Tabellen aus Nr. 169) und ruft
 * `plot_rolle_stand`, `plot_rolle_erfassen`, `plot_druck_nachtragen`,
 * `plot_druck_nachtrag_loeschen` und `plot_druck_uebersicht` aus Nr. 170;
 * die Sperre „Rolle reicht nicht" sitzt als Trigger auf `plot_auftraege`
 * (ebenfalls Nr. 170). Ohne beide liefert der Client fail-open `null` bzw.
 * eine leere Liste — dann wird nie gesperrt, „Druck nachtragen" und das
 * Erfassen der Rolle scheitern aber mit „nicht eingerichtet"; die
 * Spaltenwache (Test unten) sieht das nicht. Davor 168:
 * SiFa-Gefaehrdungsbeurteilung —
 * der Client liest und schreibt die zehn GBU-Tabellen aus Nr. 167
 * (Kataster, GBU, Fassungen, Freigabe-Links; fail-open bei PGRST205, dann
 * bleibt die Kachel leer), legt das Fassungs-PDF mit
 * `project_documents.erzeugt_aus = 'gbu'` ab (ohne Nr. 167 23514) und ruft
 * die RPCs aus Nr. 168 — `sifa_gbu_fassung_einfrieren`,
 * `sifa_gbu_freigeben`, `sifa_gbu_freigabe_link_anlegen`,
 * `sifa_gbu_freigabe_zurueckziehen`, `sifa_taetigkeit_gebucht_markieren`
 * und oeffentlich `sifa_gbu_freigabe_public` /
 * `sifa_gbu_freigabe_unterschreiben` (ohne sie PGRST202, Freigabe und
 * „Zeit buchen" gingen ins Leere). Die Spaltenwache (Test unten) sieht das
 * nicht, sie kennt nur `projects`, `project_services` und
 * `inspection_items`. Davor 166: Paketposition mit Bestandteilen —
 * der Client waehlt `project_services.bestandteile` und
 * `project_services.bestandteile_offen` in PROJECT_SERVICES_COLUMNS mit
 * (Spalten aus Nr. 165; ohne sie antwortet PostgREST mit 42703 und es laedt
 * KEIN Projekt mehr — die Spaltenwache im Test unten sieht das) und ruft die
 * RPCs `bestandteil_erledigen` / `bestandteil_entfaellt` /
 * `bestandteil_zuruecknehmen` aus Nr. 166 (ohne sie PGRST202); erst Nr. 166
 * rechnet `bestandteile_offen` — ohne sie bleibt jede Paketposition
 * gesperrt (Gate unbekannt). Davor 163: `projekt_unterlagen_ausgaben`
 * und `unterlagen_fassungsentscheide`, Ausgabenjournal und Fassungsentscheide
 * der Folgeunterlagen (Plan Stufe 6) — der Client schreibt eine Ausgabe bei
 * Freigabe und Versand und den Entscheid aus der Rueckfrage des
 * Aenderungsdienstes (ohne Nr. 163 antwortet PostgREST mit PGRST205,
 * sichtbar gemeldet) und liest beide Tabellen fail-open;
 * `planung_erinnerungen` beschreibt nur der Nachtlauf mit service_role. Die
 * Spaltenwache (Test unten) sieht das nicht, sie kennt nur `projects`,
 * `project_services` und `inspection_items`. Davor 162:
 * `planungsphasen.empfaenger`,
 * Empfaenger je Adressat — der Client liest die Spalte beim Oeffnen der
 * Fragenliste (`fetchEmpfaenger`, fail-open mit Hinweis) und schreibt sie
 * per Teil-Update (`speichereEmpfaenger`); ohne Nr. 162 antwortet PostgREST
 * mit PGRST204 und das Zuordnen scheitert; die Spaltenwache (Test unten)
 * sieht das nicht, sie kennt nur `projects`, `project_services` und
 * `inspection_items`. Davor 161: Erzeugte Unterlagen in der
 * Projektablage — der Client schickt `project_documents.erzeugt_aus` und
 * `fassung_ref` beim Ablegen einer erzeugten Unterlage mit und legt sie in
 * der Kategorie `eigene_unterlagen` ab; Spalten, CHECKs und Index kommen
 * mit Nr. 160, Loesch- und Aenderungsschutz (Definer-Funktionen,
 * RESTRICTIVE DELETE-Policies, BEFORE-UPDATE-Riegel auf erzeugt_aus/
 * fassung_ref) erst mit Nr. 161 (wegen der Editor-Regel getrennt).
 * Ohne Nr. 160 antwortet PostgREST mit PGRST204 bzw. 23514 und das Ablegen
 * scheitert; die Spaltenwache (Test unten) sieht das nicht, sie kennt nur
 * `projects`, `project_services` und `inspection_items`. Davor 159:
 * `close_expired_project_deletion_requests` (Loeschantraege; der Client
 * ruft sie fail-open). Davor 158: Plotten aus der App — der
 * Client liest `plot_auftraege` mit namentlicher Spaltenliste
 * (`PLOT_AUFTRAG_SPALTEN`) und ruft `plot_status`, `plot_auftrag_anlegen`,
 * `plot_auftrag_abbrechen`, `plot_auftrag_klaeren`, `plot_agent_anlegen` und
 * `plot_agent_sperren`; die Tabellen und der Bucket `plot-auftraege` kommen
 * mit Nr. 157, die RPCs erst mit Nr. 158 (wegen der Editor-Regel getrennt).
 * Ohne beide antwortet PostgREST mit PGRST205/PGRST202, und der Knopf „An
 * Plotter senden" bleibt beim Zustand „nicht eingerichtet"; die Spaltenwache
 * (Test unten) sieht auch das nicht. Davor 156: `sige_arbeitsschritte.
 * mitgeltende_dokumente`, `sige_arbeiten_katalog.mitgeltende_dokumente` und
 * `planungsphasen.unterlagen` — der Client schickt sie bei Insert und Update
 * mit, ohne Nr. 156 antwortet PostgREST mit PGRST204 und das Speichern im
 * SiGe-Plan, im Arbeitenkatalog und in der Planungsphase scheitert; die
 * Spaltenwache (Test unten) sieht das nicht, sie kennt nur `projects`,
 * `project_services` und `inspection_items`. Davor 130:
 * `inspection_items.plan_id` /
 * `plan_coordinates` / `verortung`, Planverortung — der Client liest die
 * Nadeln beim Laden und schreibt sie bei jedem Speichern mit; der
 * Direkt-Upsert in `restoreInspectionItems` faellt ohne Nr. 130 auf eine
 * Wiederholung ohne die Spalten zurueck. Davor 96, Bauphase-Fenster: der Client
 * ruft `maintenance_start` mit VIER Parametern (Vorlauf, Text, Fensterlaenge,
 * ausgelieferter Build) und `maintenance_release` — gegen die Zwei-Parameter-
 * Fassung aus Nr. 91 antwortet PostgREST mit PGRST202, der Admin-Knopf ginge
 * ins Leere; Nr. 95 legt die Spalte `app_maintenance.deployed_build` an,
 * Nr. 96 die Ruempfe. Davor 91, Bauphase: der Client ruft
 * `maintenance_status` / `maintenance_start` / `maintenance_end` per RPC
 * und hoert per Realtime auf `app_maintenance`. Die Tabelle legt Nr. 90 an,
 * die RPCs, der Trigger und die Realtime-Publikation kommen erst mit Nr. 91
 * (`app_maintenance_rpc.sql`, wegen der Editor-Regel getrennt) — ohne
 * Nr. 91 antwortet PostgREST mit PGRST202, der Admin-Knopf ginge ins Leere
 * und kein Client saehe einen Countdown; davor 89, `ufsa_rab_entries.sort_order`)
 * — und seitdem wird das Vergessen bemerkt:
 * `scripts/db/vorausgesetzteSpalten.test.ts` wird rot, sobald diese Zahl
 * KLEINER ist als die hoechste Migration, die eine vom Client abgefragte
 * Spalte anlegt. Von Hand hochsetzen bleibt die Regel; es unbemerkt zu
 * vergessen geht nicht mehr.
 */
export const ERWARTETE_MIGRATION = 185;

/** Ergebnis der Wache, so wie die Anzeige es unveraendert verwenden kann. */
export interface SchemaStand {
    erwartet: number;
    /**
     * `max(nummer)` aus der Tabelle. `null`, wenn die Tabelle leer ist —
     * das ist NICHT dasselbe wie „Stand 0": leer heisst, dass noch kein
     * `db:adopt` gelaufen ist.
     */
    vorhanden: number | null;
    /** Der Client verlangt mehr, als die Umgebung hat. */
    hinterher: boolean;
    /** Fertiger Bannertext; leer, solange `hinterher` falsch ist. */
    text: string;
}

/**
 * Die reine Entscheidung: was gilt als hinterher, und was steht dann da.
 *
 * Bewusst ohne jeden Datenbankbezug, damit die Faelle, die sonst nur in einer
 * ausgerollten Umgebung sichtbar wuerden (leere Tabelle, Umgebung VOR dem
 * Client, Umgebung VOR-aus), hier im Test stehen.
 *
 * Eine Umgebung, die WEITER ist als der Client (jemand hat SQL gefahren und
 * den alten Client offen), ist ausdruecklich kein Fall fuer den Balken:
 * Migrationen sind aufwaertskompatibel gebaut, und eine Warnung, die bei
 * jedem normalen Ausrollen kurz aufleuchtet, erzieht zum Wegsehen.
 */
export const bewerteSchemaStand = (erwartet: number, vorhanden: number | null): SchemaStand => {
    const hinterher = vorhanden === null || vorhanden < erwartet;
    if (!hinterher) return { erwartet, vorhanden, hinterher: false, text: '' };

    // Leere Tabelle liest sich anders als ein Rueckstand: hier fehlt nicht
    // eine Migration, hier fehlt die Uebernahme des Bestands. Wer darauf
    // `db:migrate` wirft, loest einen Voll-Replay aus — der setzt u. a.
    // gesetzte Zahlungsziele auf NULL zurueck. Deshalb nennt der Text in
    // diesem Fall zuerst `db:adopt`.
    const stand = vorhanden === null ? 'keine Eintraege' : `vorhanden ${vorhanden}`;
    const rat =
        vorhanden === null
            ? 'Bestand mit „npm run db:adopt" uebernehmen, danach „npm run db:migrate".'
            : 'Fehlende Migrationen mit „npm run db:migrate" fahren.';

    return {
        erwartet,
        vorhanden,
        hinterher: true,
        text: `Datenbank hinkt hinterher: erwartet ${erwartet}, ${stand}. ${rat}`,
    };
};

/** PostgREST liefert je nach Schema-Cache 42P01 oder PGRST205. */
const errorCode = (e: unknown): string => {
    const c = (e as { code?: unknown })?.code;
    return typeof c === 'string' ? c : '';
};
const istFehlendeTabelle = (e: unknown): boolean => {
    const code = errorCode(e);
    return code === 'PGRST205' || code === '42P01';
};

/**
 * Liest `max(nummer)` aus `public.schema_migrations` und bewertet ihn.
 *
 * `null` heisst: dazu ist nichts zu sagen — die Tabelle gibt es in dieser
 * Umgebung noch nicht (Nr. 57 nicht gefahren). Das ist der Normalfall
 * WAEHREND des Ausrollens und deshalb der einzige fail-open-Fall: eine
 * Warnung „Ihre Datenbank ist veraltet", die ausgerechnet die Umgebung
 * anzeigt, in der die Wache selbst noch nicht existiert, waere Unsinn.
 *
 * JEDER ANDERE Fehler wird weitergereicht — Netz, Rechte, abgelaufener
 * Token. Ein stiller Rueckfall auf „alles in Ordnung" ist genau die Klasse
 * Fehlaussage, gegen die diese Wache gebaut wurde: sie behauptete dann
 * Gesundheit, obwohl die Abfrage gar nicht stattgefunden hat.
 *
 * Kein `count`, kein Voll-Select: eine Zeile, absteigend sortiert. Die
 * Tabelle ist klein, aber das Muster soll es auch in zehn Jahren sein.
 */
export const pruefeSchemaStand = async (): Promise<SchemaStand | null> => {
    try {
        const { data, error } = await supabase
            .from('schema_migrations')
            .select('nummer')
            .order('nummer', { ascending: false })
            .limit(1);
        if (error) throw error;

        const roh = (data || [])[0]?.nummer;
        const zahl = Number(roh);
        // Leere Tabelle → `null`. Nicht 0: „noch nichts uebernommen" ist ein
        // eigener Zustand mit einem eigenen Rat (siehe `bewerteSchemaStand`).
        const vorhanden = Number.isFinite(zahl) ? zahl : null;
        return bewerteSchemaStand(ERWARTETE_MIGRATION, vorhanden);
    } catch (e) {
        if (istFehlendeTabelle(e)) {
            console.warn(
                'schema_migrations fehlt — supabase/schema_migrations.sql (Nr. 57) noch nicht gefahren.',
            );
            return null;
        }
        throw e;
    }
};
